import {
  AltiusRes,
  CashflowGraph,
  CurrencyType,
  DateRange,
  ExchangeMethod,
  ReportAllocationTable,
  ReportCashflowAllocationTarget,
  ReportCashflowAum,
  ReportCashflowPreset,
  ReportCashflowSet,
  ReportCashflowSetting,
  ReportFilters,
  ReportPredictionTable,
  ReportsQueries,
  UpdateAllocationTargetPayload,
  UpdateCashflowAum
} from "store/types";
import downloadFileAxios from "utils/download-file-axios";

import { AsyncActions, AsyncActionsParams } from "../base";

export interface ICashflowActions {
  getCashflowTableAction: (params: {
    set_id: number;
    filters?: ReportFilters;
    currency: CurrencyType;
    dateRange: DateRange;
    exchangeDate: ExchangeMethod;
    use_accounting_date: boolean;
    show_deal?: boolean;
  }) => Promise<ReportPredictionTable>;

  getCashflowSetsAction: () => Promise<Array<ReportCashflowSet>>;
  deleteCashflowSetAction: (id: number) => Promise<void>;
  createCashflowSetAction: (title: string, params?: string) => Promise<ReportCashflowSet>;

  getCashflowSettingsAction: (data: {
    set_id: number;
    asset_class_id?: number;
    sub_asset_class_id?: number;
    deal_id?: number;
  }) => Promise<ReportCashflowSetting | []>;
  saveCashflowSettingsAction: (
    data: Partial<Omit<ReportCashflowSetting, "id" | "set_id" | "preset_id">> &
      Required<Pick<ReportCashflowSetting, "set_id" | "preset_id">> & {
        // parameters_conflict?: "overwrite_all" | "overwrite_default_only";
        override?: string;
      }
  ) => Promise<{ parameters_conflict: boolean }>;
  resetCashflowSettingsAction: (data: {
    set_id: number;
    asset_class_id?: number;
    sub_asset_class_id?: number;
    deal_id?: number;
  }) => Promise<void>;
  saveCashflowSettingsCellAction: (data: {
    set_id: number;
    asset_class_id?: number;
    sub_asset_class_id?: number;
    deal_id?: number;
    year: number;
    type: string;
    value: string;
    deal_values: Array<{ id: number; value: number; user_id: number | null }>;
    // parameters_conflict?: "overwrite_all" | "overwrite_default_only";
    override?: string;
  }) => Promise<{ parameters_conflict: boolean }>;
  resetCashflowSettingsCellAction: (data: {
    set_id: number;
    asset_class_id?: number;
    sub_asset_class_id?: number;
    deal_id?: number;
    year: number;
    type: string;
  }) => Promise<void>;

  getCashflowPresetsAction: () => Promise<Array<ReportCashflowPreset>>;

  getCashflowGraphAction: (
    data: Pick<ReportsQueries, "currency" | "dateRange" | "filters" | "use_accounting_date"> & {
      exchangeDate: string;
      set_id: number;
    }
  ) => Promise<CashflowGraph>;

  getCashflowAumAction: (data: { set_id: number }) => Promise<ReportCashflowAum | null>;

  updateCashflowAumAction: (data: UpdateCashflowAum) => Promise<ReportCashflowAum>;

  getAllocationTargetAction: (data: { aum_id: number }) => Promise<ReportCashflowAllocationTarget[]>;
  updateAllocationTargetAction: (data: {
    aum_id: number;
    targets: UpdateAllocationTargetPayload[];
  }) => Promise<ReportCashflowAllocationTarget[]>;

  getAllocationTableAction: (params: {
    set_id: number;
    filters?: ReportFilters;
    currency: CurrencyType;
    dateRange: DateRange;
    exchangeDate: ExchangeMethod;
  }) => Promise<ReportAllocationTable>;

  downloadCashflowTableAction: (data: {
    set_id: number;
    filters?: ReportFilters;
    currency: CurrencyType;
    dateRange: DateRange;
    exchangeDate: ExchangeMethod;
  }) => Promise<void>;
  getCashflowGraphDealsAction: () => Promise<number>;
}

export class CashflowActions extends AsyncActions implements ICashflowActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getCashflowTableAction: ICashflowActions["getCashflowTableAction"] = async (body) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ReportPredictionTable>>({
      url: "/api/v0.0.3/reports/get-cashflow-table",
      data: body,
      signal: this.addProcess("getCashflowTableAction").signal
    });
    this.removeProcess("getCashflowTableAction");

    return payload;
  };

  public getCashflowSetsAction: ICashflowActions["getCashflowSetsAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ReportCashflowSet[]>>({
      url: "/api/v0.0.3/cashflow/sets"
    });

    return payload;
  };

  public createCashflowSetAction: ICashflowActions["createCashflowSetAction"] = async (
    name: string,
    params?: string
  ) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ReportCashflowSet>>({
      url: "/api/v0.0.3/cashflow/sets",
      data: { name, params }
    });

    return payload;
  };

  public deleteCashflowSetAction: ICashflowActions["deleteCashflowSetAction"] = async (id: number) => {
    await this.api.delete({ url: `/api/v0.0.3/cashflow/sets/${id}` });
  };

  public getCashflowSettingsAction: ICashflowActions["getCashflowSettingsAction"] = async (params) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ReportCashflowSetting | []>>({
      url: "/api/v0.0.3/cashflow/settings",
      params
    });

    return payload;
  };

  public saveCashflowSettingsAction: ICashflowActions["saveCashflowSettingsAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<{ parameters_conflict: boolean } | []>>({
      url: "/api/v0.0.3/cashflow/settings",
      data
    });

    return Array.isArray(payload) ? { parameters_conflict: false } : payload;
  };

  public resetCashflowSettingsAction: ICashflowActions["resetCashflowSettingsAction"] = async (data) => {
    await this.api.post<AltiusRes<{ parameters_conflict: boolean } | []>>({
      url: "/api/v0.0.3/cashflow/settings",
      data: { ...data, reset: true }
    });
  };

  public saveCashflowSettingsCellAction: ICashflowActions["saveCashflowSettingsCellAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<{ parameters_conflict: boolean } | []>>({
      url: "/api/v0.0.3/cashflow/cell-settings",
      data
    });

    return Array.isArray(payload) ? { parameters_conflict: false } : payload;
  };

  public resetCashflowSettingsCellAction: ICashflowActions["resetCashflowSettingsCellAction"] = async (data) => {
    await this.api.post<AltiusRes<{ parameters_conflict: boolean } | []>>({
      url: "/api/v0.0.3/cashflow/cell-settings",
      data: { ...data, reset: true }
    });
  };

  public getCashflowPresetsAction: ICashflowActions["getCashflowPresetsAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ReportCashflowPreset[]>>({
      url: "/api/v0.0.3/cashflow/presets"
    });

    return payload;
  };

  public getCashflowGraphAction: ICashflowActions["getCashflowGraphAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<CashflowGraph>>({
      url: "/api/v0.0.3/reports/get-cashflow-graph",
      data: { ...data },
      signal: this.addProcess("getCashflowGraphAction").signal
    });
    this.removeProcess("getCashflowGraphAction");
    return payload;
  };

  public getCashflowAumAction: ICashflowActions["getCashflowAumAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ReportCashflowAum>>({
      url: "/api/v0.0.3/cashflow/aum",
      params: data
    });

    return payload;
  };

  public updateCashflowAumAction: ICashflowActions["updateCashflowAumAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ReportCashflowAum>>({
      url: "/api/v0.0.3/cashflow/aum",
      data
    });
    return payload;
  };

  public getAllocationTargetAction: ICashflowActions["getAllocationTargetAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ReportCashflowAllocationTarget[]>>({
      url: "/api/v0.0.3/cashflow/allocation-targets",
      params: data
    });

    return payload;
  };

  public updateAllocationTargetAction: ICashflowActions["updateAllocationTargetAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ReportCashflowAllocationTarget[]>>({
      url: "/api/v0.0.3/cashflow/allocation-targets",
      data
    });

    return payload;
  };

  public getAllocationTableAction: ICashflowActions["getAllocationTableAction"] = async (body) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ReportAllocationTable>>({
      url: "/api/v0.0.3/reports/get-allocation-table",
      params: body,
      signal: this.addProcess("getAllocationTableAction").signal
    });
    this.removeProcess("getAllocationTableAction");

    return payload;
  };

  public downloadCashflowTableAction: ICashflowActions["downloadCashflowTableAction"] = async (body) => {
    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/get-cashflow-table",
        data: body,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getCashflowGraphDealsAction: ICashflowActions["getCashflowGraphDealsAction"] = async () => {
    const {
      data: { data: response }
    } = await this.api.get<AltiusRes<{ number_of_deals: number }>>({
      url: "/api/v0.0.3/reports/get-cashflow-graph-deals",
      signal: this.addProcess("getCashflowGraphDealsAction").signal
    });
    this.removeProcess("getCashflowGraphDealsAction");
    return response.number_of_deals;
  };
}
